/**
 * Roving-focus arithmetic for the multi-property availability grid (#1582).
 *
 * Split out as pure functions because the interesting part is two-dimensional
 * and edge-heavy — properties × dates, with rows that differ in which dates
 * they even have — and none of that needs React to be tested.
 *
 * Column -1 is the property row header. It is part of the grid on purpose:
 * when `propertyLinkable` is on (the default) every row header holds an <a>,
 * and a grid with unmanaged focusable descendants is neither one tabstop nor
 * a valid APG grid. Folding the header in as column -1 keeps the single
 * tabstop AND keeps the property link reachable — dropping it to tabindex -1
 * without a home in the roving model would take away a link the keyboard can
 * reach today.
 */

/** The property-row header column. */
export const HEADER_COL = -1

export interface GridCell {
  /** Property row index. */
  row: number
  /** Date column index, or HEADER_COL for the property header. */
  col: number
}

export interface GridShape {
  rowCount: number
  colCount: number
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))

/**
 * Where a key press moves the focused cell, or null when the key is not ours.
 *
 * Movement CLAMPS at every edge rather than wrapping: the grid is a scrolling
 * window onto a paginated list, so wrapping from the last date of one property
 * to the first date of the next would read as a jump, not a step.
 *
 * Unavailable cells are landed on, never skipped. They are `aria-disabled`,
 * not absent, and APG is explicit that a grid's focus must be able to reach
 * every cell — a keyboard user has to be able to inspect a booked night's
 * price, and skipping would make some cells unreachable in a fully-booked row.
 */
export function nextCell(key: string, from: GridCell, shape: GridShape): GridCell | null {
  const { rowCount, colCount } = shape
  if (rowCount === 0 || colCount === 0) return null

  const lastCol = colCount - 1
  const lastRow = rowCount - 1
  const move = (row: number, col: number): GridCell => ({
    row: clamp(row, 0, lastRow),
    col: col === HEADER_COL ? HEADER_COL : clamp(col, 0, lastCol),
  })

  switch (key) {
    case 'ArrowRight':
      // Out of the header and onto the first date, then along the row.
      return from.col === HEADER_COL ? move(from.row, 0) : move(from.row, from.col + 1)
    case 'ArrowLeft':
      // Stepping left off the first date returns to the property header.
      if (from.col === HEADER_COL) return from
      return from.col === 0 ? { row: from.row, col: HEADER_COL } : move(from.row, from.col - 1)
    case 'ArrowUp':
      return { row: clamp(from.row - 1, 0, lastRow), col: from.col }
    case 'ArrowDown':
      return { row: clamp(from.row + 1, 0, lastRow), col: from.col }
    case 'Home':
      /* Row edges, per the issue. APG also defines Ctrl+Home / Ctrl+End as the
         grid's first and last cell, and this does NOT implement them: the grid
         ignores every modified key so it cannot swallow Back (Cmd/Alt+Arrow) or
         document scroll (Ctrl+Home). Both are optional in the pattern, and
         keeping browser shortcuts working matters more for this feature's
         users than the two extra jumps. */
      return { row: from.row, col: HEADER_COL }
    case 'End':
      return { row: from.row, col: lastCol }
    case 'PageUp':
      return { row: clamp(from.row - PAGE_ROWS, 0, lastRow), col: from.col }
    case 'PageDown':
      return { row: clamp(from.row + PAGE_ROWS, 0, lastRow), col: from.col }
    default:
      return null
  }
}

/**
 * Rows a PageUp/PageDown jumps.
 *
 * The property calendar pages by MONTH because its axes are days-and-weeks.
 * Here the vertical axis is a property list with no natural grouping, so the
 * useful unit is "a screenful of rows" and the number is simply a choice.
 */
export const PAGE_ROWS = 10

/** The keys the grid consumes. Anything else must reach the browser. */
export function isGridNavigationKey(key: string): boolean {
  return (
    key === 'ArrowLeft' || key === 'ArrowRight' || key === 'ArrowUp' || key === 'ArrowDown' ||
    key === 'Home' || key === 'End' || key === 'PageUp' || key === 'PageDown'
  )
}

/**
 * Keep a remembered cell inside a grid that has changed shape underneath it.
 *
 * The property list grows when "Load more" runs and is replaced wholesale when
 * the query key changes (a new date window or page size), so a remembered
 * position can point past the end. Clamping beats dropping to the top-left:
 * the user keeps roughly their place instead of being thrown to the start.
 */
export function clampToShape(cell: GridCell, shape: GridShape): GridCell | null {
  if (shape.rowCount === 0 || shape.colCount === 0) return null
  return {
    row: clamp(cell.row, 0, shape.rowCount - 1),
    col: cell.col === HEADER_COL ? HEADER_COL : clamp(cell.col, 0, shape.colCount - 1),
  }
}
